import { useEffect, useMemo, useState } from 'react'
import {
  Combobox,
  Loader,
  ScrollArea,
  TextInput,
  useCombobox,
  type ComboboxProps,
} from '@mantine/core'
import { IconSearch, IconX } from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import type { Patient } from '@medplum/fhirtypes'
import { useDebounce } from '@/hooks'
import { formatPatientName } from '@/utils'
import { buildPatientLookupSearchQuery } from '@/utils/patientSearch'
import { logError } from '@/utils/errorUtils'
import { cn } from '@/utils/cn'

type GlobalPatientSearchProps = {
  placeholder?: string
  className?: string
  inputClassName?: string
  comboboxProps?: ComboboxProps
  onPick: (patient: { id: string; name: string }) => void
}

/**
 * Compact, header-friendly patient search.
 *
 * Wraps Mantine's `Combobox` around a `TextInput` (so we can colocate the
 * search/clear adornments) and queries `Patient` resources via Medplum's
 * standard `_filter` lookup. The picker is intentionally minimal — the
 * caller decides what to do with the selection (navigate, open a panel,
 * etc.) through `onPick`.
 */
export function GlobalPatientSearch({
  placeholder = 'Search patients...',
  className,
  inputClassName,
  comboboxProps,
  onPick,
}: GlobalPatientSearchProps) {
  const medplum = useMedplum()
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<Patient[]>([])
  const debouncedQuery = useDebounce(query, 300)

  useEffect(() => {
    const trimmed = debouncedQuery.trim()
    if (trimmed.length < 2) {
      setResults([])
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    const filter = buildPatientLookupSearchQuery(trimmed)
    medplum
      .search('Patient', filter)
      .then((bundle) => {
        if (cancelled) return
        const patients =
          bundle.entry
            ?.map((entry) => entry.resource as Patient | undefined)
            .filter((p): p is Patient => !!p?.id) ?? []
        setResults(patients.slice(0, 8))
      })
      .catch((err) => {
        if (cancelled) return
        logError('shell', 'Global patient search failed', err)
        setResults([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [debouncedQuery, medplum])

  const options = useMemo(
    () =>
      results.map((patient) => {
        const name = formatPatientName(patient.name?.[0])
        return (
          <Combobox.Option value={patient.id!} key={patient.id}>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-[#313131]">
                {name || 'Unknown patient'}
              </span>
              {patient.birthDate && (
                <span className="text-xs text-[#a5a1ad]">DOB {patient.birthDate}</span>
              )}
            </div>
          </Combobox.Option>
        )
      }),
    [results],
  )

  const handleSubmit = (value: string) => {
    const patient = results.find((p) => p.id === value)
    if (!patient?.id) return
    const name = formatPatientName(patient.name?.[0]) || 'Patient'
    onPick({ id: patient.id, name })
    setQuery('')
    setResults([])
    combobox.closeDropdown()
  }

  return (
    <Combobox
      store={combobox}
      withinPortal
      shadow="md"
      onOptionSubmit={handleSubmit}
      {...comboboxProps}
    >
      <Combobox.Target>
        <TextInput
          placeholder={placeholder}
          value={query}
          onChange={(event) => {
            setQuery(event.currentTarget.value)
            combobox.openDropdown()
            combobox.updateSelectedOptionIndex()
          }}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          leftSection={<IconSearch size={14} className="opacity-60" />}
          rightSection={
            loading ? (
              <Loader size="xs" />
            ) : query ? (
              <IconX
                size={14}
                className="cursor-pointer opacity-60"
                onMouseDown={(event) => {
                  event.preventDefault()
                  setQuery('')
                  setResults([])
                }}
              />
            ) : null
          }
          className={cn('w-[240px]', className)}
          classNames={{
            input: cn(
              'h-9 rounded-md border border-gray-200 bg-gray-50 text-sm focus:border-primary focus:bg-white',
              inputClassName,
            ),
          }}
          aria-label={placeholder}
        />
      </Combobox.Target>

      <Combobox.Dropdown hidden={query.trim().length < 2}>
        <Combobox.Options>
          <ScrollArea.Autosize mah={280} type="scroll">
            {loading && (
              <Combobox.Empty>
                <span className="text-xs text-[#a5a1ad]">Searching...</span>
              </Combobox.Empty>
            )}
            {!loading && options.length === 0 && (
              <Combobox.Empty>
                <span className="text-xs text-[#a5a1ad]">No patients found</span>
              </Combobox.Empty>
            )}
            {!loading && options}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
